import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/lib/api';
import { formatCurrency, calculateWeeks, getWeekLabels } from '@/lib/dateUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Edit, User, TrendingUp, TrendingDown, Minus, Save, X, Plus, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const ELIGIBLE_STATUSES = ['sow', 'execute'];

const getUtilizationStatus = (pct) => {
  if (pct <= 80) return { label: 'Under Utilized', color: 'text-red-400 bg-red-400/10 border-red-400/20' };
  if (pct <= 100) return { label: 'Optimal', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' };
  return { label: 'Over Utilized', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' };
};

const getUtilizationIcon = (pct) => {
  if (pct <= 80) return <TrendingDown className="w-3.5 h-3.5" />;
  if (pct <= 100) return <Minus className="w-3.5 h-3.5" />;
  return <TrendingUp className="w-3.5 h-3.5" />;
};

const calcTotal = (resources) => {
  return (resources || []).reduce((sum, res) => {
    const resTotal = (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0);
    return sum + resTotal;
  }, 0);
};

// Group flat actualResources array by resource type
const groupByType = (actualResources, plannedResources) => {
  const groups = [];
  const seenTypes = new Set();

  // Use planned resources to define ordering
  (plannedResources || []).forEach(pr => {
    if (seenTypes.has(pr.type)) return;
    seenTypes.add(pr.type);
    const persons = (actualResources || []).filter(ar => ar.type === pr.type);
    groups.push({ type: pr.type, label: pr.label, rate: pr.rate, planned: pr, persons });
  });

  // Include any actual resource types not in planned
  (actualResources || []).forEach(ar => {
    if (!seenTypes.has(ar.type)) {
      seenTypes.add(ar.type);
      groups.push({ type: ar.type, label: ar.label, rate: ar.rate, planned: null, persons: [ar] });
    }
  });

  return groups;
};

export function ActualVsPlanned({ projects, onProjectsRefresh }) {
  const [editProject, setEditProject] = useState(null);
  const [actualResources, setActualResources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applyAllWeeks, setApplyAllWeeks] = useState({});
  const [spilloverCount, setSpilloverCount] = useState(0);
  const [backendMetrics, setBackendMetrics] = useState(null); // Backend-calculated utilization metrics

  // Fetch backend utilization metrics on mount
  useEffect(() => {
    if (api.utilizationMetrics) {
      api.utilizationMetrics()
        .then(metrics => setBackendMetrics(metrics))
        .catch(() => setBackendMetrics(null));
    }
  }, []);

  const eligibleProjects = useMemo(() => {
    return projects.filter(p => ELIGIBLE_STATUSES.includes(p.status));
  }, [projects]);

  const projectMetrics = useMemo(() => {
    return eligibleProjects.map(p => {
      // Try to get backend metrics if available, otherwise use frontend calculation
      const backendMetric = backendMetrics?.find(m => m.project_id === p.id);
      
      let planned, actual, pct;
      if (backendMetric) {
        planned = backendMetric.planned_hours || 0;
        actual = backendMetric.actual_hours || planned;
        pct = backendMetric.utilization_pct || (planned > 0 ? Math.round((actual / planned) * 100) : 0);
      } else {
        planned = calcTotal(p.resources);
        const hasActuals = (p.actualResources || []).length > 0;
        actual = hasActuals ? calcTotal(p.actualResources) : planned;
        pct = planned > 0 ? Math.round((actual / planned) * 100) : 0;
      }
      
      const status = getUtilizationStatus(pct);
      const hasActuals = (p.actualResources || []).length > 0;
      return { ...p, planned, actual, pct, status, hasActuals };
    });
  }, [eligibleProjects, backendMetrics]);

  const openEdit = (project) => {
    const plannedWeeks = calculateWeeks(project.startDate, project.endDate);
    const hasActuals = (project.actualResources || []).length > 0;

    const actuals = hasActuals
      ? project.actualResources.map(ar => ({
          type: ar.type, label: ar.label, name: ar.name || '', rate: ar.rate,
          weeklyAllocation: [...(ar.weeklyAllocation || [])],
        }))
      : (project.resources || []).map(r => ({
          type: r.type, label: r.label, name: '', rate: r.rate,
          weeklyAllocation: [...(r.weeklyAllocation || Array(plannedWeeks).fill(0))],
        }));

    // Determine max weeks (some actuals may already have spillover)
    const maxWeeks = Math.max(plannedWeeks, ...actuals.map(a => (a.weeklyAllocation || []).length));
    // Pad all actuals to maxWeeks
    actuals.forEach(a => {
      while (a.weeklyAllocation.length < maxWeeks) a.weeklyAllocation.push(0);
    });

    setSpilloverCount(Math.max(0, maxWeeks - plannedWeeks));
    setActualResources(actuals);
    setApplyAllWeeks({});
    setEditProject(project);
  };

  const addSpilloverWeek = () => {
    setActualResources(prev => prev.map(r => ({
      ...r,
      weeklyAllocation: [...(r.weeklyAllocation || []), 0],
    })));
    setSpilloverCount(prev => prev + 1);
  };

  const updatePersonName = (flatIdx, name) => {
    setActualResources(prev => {
      const arr = [...prev];
      arr[flatIdx] = { ...arr[flatIdx], name };
      return arr;
    });
  };

  const updatePersonAllocation = (flatIdx, weekIdx, value) => {
    setActualResources(prev => {
      const arr = [...prev];
      const alloc = [...(arr[flatIdx].weeklyAllocation || [])];
      const numVal = Number(value) || 0;
      if (applyAllWeeks[flatIdx]) {
        // Apply to all weeks
        for (let i = 0; i < alloc.length; i++) alloc[i] = numVal;
      } else {
        alloc[weekIdx] = numVal;
      }
      arr[flatIdx] = { ...arr[flatIdx], weeklyAllocation: alloc };
      return arr;
    });
  };

  const toggleApplyAll = (flatIdx, checked) => {
    setApplyAllWeeks(prev => ({ ...prev, [flatIdx]: checked }));
    if (checked) {
      // Apply first week value to all weeks
      setActualResources(prev => {
        const arr = [...prev];
        const alloc = [...(arr[flatIdx].weeklyAllocation || [])];
        const firstVal = alloc[0] || 0;
        for (let i = 0; i < alloc.length; i++) alloc[i] = firstVal;
        arr[flatIdx] = { ...arr[flatIdx], weeklyAllocation: alloc };
        return arr;
      });
    }
  };

  const addPerson = (type, label, rate) => {
    const totalWeeks = actualResources.length > 0 ? (actualResources[0].weeklyAllocation || []).length : (editProject ? calculateWeeks(editProject.startDate, editProject.endDate) : 0);
    setActualResources(prev => [
      ...prev,
      { type, label, name: '', rate, weeklyAllocation: Array(totalWeeks).fill(0) },
    ]);
  };

  const removePerson = (flatIdx) => {
    setActualResources(prev => prev.filter((_, i) => i !== flatIdx));
  };

  const handleSave = async () => {
    if (!editProject) return;
    setSaving(true);
    try {
      await api.saveActualResources(editProject.id, actualResources);
      toast.success('Actual utilization saved');
      setEditProject(null);
      if (onProjectsRefresh) onProjectsRefresh();
    } catch {
      toast.error('Failed to save actual utilization');
    } finally {
      setSaving(false);
    }
  };

  const editPlannedWeeks = editProject ? calculateWeeks(editProject.startDate, editProject.endDate) : 0;
  const editTotalWeeks = editPlannedWeeks + spilloverCount;
  const editWeekLabels = useMemo(() => {
    const labels = editProject ? getWeekLabels(editProject.startDate, editPlannedWeeks) : [];
    for (let i = 0; i < spilloverCount; i++) {
      labels.push(`W${editPlannedWeeks + i + 1}+`);
    }
    return labels;
  }, [editProject, spilloverCount, editPlannedWeeks]);
  const editPlannedTotal = editProject ? calcTotal(editProject.resources) : 0;
  const editActualTotal = calcTotal(actualResources);
  const editPct = editPlannedTotal > 0 ? Math.round((editActualTotal / editPlannedTotal) * 100) : 0;
  const editStatus = getUtilizationStatus(editPct);

  // Group for dialog rendering
  const editGroups = useMemo(() => {
    return editProject ? groupByType(actualResources, editProject.resources) : [];
  }, [editProject, actualResources]);

  // Build flat index map: for each group/person, what's their index in actualResources[]
  const flatIndexMap = useMemo(() => {
    const map = [];
    editGroups.forEach(group => {
      const indices = group.persons.map(person => actualResources.indexOf(person));
      map.push(indices);
    });
    return map;
  }, [editGroups, actualResources]);

  return (
    <>
      {eligibleProjects.length === 0 ? (
        <Card className="bg-zinc-950 border-zinc-900">
          <CardContent className="p-12 text-center">
            <TrendingUp className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
            <h3 className="font-manrope font-bold text-lg text-zinc-300 mb-2">No SoW/Execute Projects</h3>
            <p className="text-zinc-500 text-sm">Move projects to SoW or Execute status to track actual utilization.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Totals Summary */}
          {(() => {
            const totalPlanned = projectMetrics.reduce((s, p) => s + p.planned, 0);
            const totalActual = projectMetrics.reduce((s, p) => s + p.actual, 0);
            const totalPct = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 100) : 0;
            const totalStatus = getUtilizationStatus(totalPct);
            return (
              <div className="grid grid-cols-3 gap-4 mb-4" data-testid="avp-totals">
                <Card className="bg-zinc-950 border-zinc-900">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Total Planned Budget</p>
                    <p className="font-mono text-xl font-bold text-white" data-testid="avp-total-planned">{formatCurrency(totalPlanned)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-950 border-zinc-900">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Total Actual Utilization</p>
                    <p className="font-mono text-xl font-bold text-brand-400" data-testid="avp-total-actual">{formatCurrency(totalActual)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-950 border-zinc-900">
                  <CardContent className="p-4 flex flex-col items-center justify-center">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Overall Utilization</p>
                    <div className="flex items-center gap-2">
                      {getUtilizationIcon(totalPct)}
                      <span className="font-mono text-xl font-bold" data-testid="avp-total-pct">{totalPct}%</span>
                      <Badge className={`${totalStatus.color} border text-xs rounded-full`}>{totalStatus.label}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          <Card className="bg-zinc-950 border-zinc-900" data-testid="actual-vs-planned-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-zinc-900">
              <h3 className="font-manrope font-bold text-base text-white">Actual vs Planned Utilization</h3>
              <p className="text-zinc-500 text-xs mt-0.5">{eligibleProjects.length} project{eligibleProjects.length !== 1 ? 's' : ''} tracked</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Lead</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">Planned Budget</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">Actual Utilization</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Utilization %</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectMetrics.map((p) => (
                  <TableRow key={p.id} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`avp-row-${p.id}`}>
                    <TableCell className="font-mono text-xs text-brand-500">{p.id}</TableCell>
                    <TableCell className="font-manrope text-sm text-white">{p.projectName || 'Untitled'}</TableCell>
                    <TableCell className="font-manrope text-sm text-zinc-300">{p.clientName || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="font-manrope text-sm text-white">{p.projectLead || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-zinc-300 text-right">{formatCurrency(p.planned)}</TableCell>
                    <TableCell className="font-mono text-sm text-right" data-testid={`avp-actual-${p.id}`}>
                      <span className={p.hasActuals ? 'text-brand-400' : 'text-zinc-500'}>{formatCurrency(p.actual)}</span>
                      {!p.hasActuals && <span className="text-[10px] text-zinc-600 ml-1">(est)</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getUtilizationIcon(p.pct)}
                        <span className="font-mono text-sm font-semibold" data-testid={`avp-pct-${p.id}`}>{p.pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${p.status.color} border text-xs rounded-full`} data-testid={`avp-status-${p.id}`}>
                        {p.status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(p)}
                        data-testid={`avp-edit-${p.id}`}
                        className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 h-8 w-8 p-0"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </>
      )}

      {/* Edit Actual Resources Dialog */}
      <Dialog open={!!editProject} onOpenChange={(open) => { if (!open) setEditProject(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="avp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white text-lg">
              Edit Actual Utilization — <span className="text-brand-400">{editProject?.projectName}</span>
              <span className="text-zinc-500 font-mono text-xs ml-2">({editProject?.id})</span>
            </DialogTitle>
          </DialogHeader>

          {editProject && (
            <div className="space-y-4">
              {/* Summary bar */}
              <div className="flex items-center gap-6 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Planned Budget</p>
                  <p className="font-mono text-sm text-zinc-300">{formatCurrency(editPlannedTotal)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Actual Utilization</p>
                  <p className="font-mono text-sm text-brand-400">{formatCurrency(editActualTotal)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Utilization</p>
                  <div className="flex items-center gap-1.5">
                    {getUtilizationIcon(editPct)}
                    <span className="font-mono text-sm font-bold">{editPct}%</span>
                    <Badge className={`${editStatus.color} border text-[10px] rounded-full ml-1`}>{editStatus.label}</Badge>
                  </div>
                </div>
              </div>

              {/* Resource groups */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="sticky left-0 z-10 bg-zinc-950 min-w-[140px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">Resource</TableHead>
                      <TableHead className="min-w-[140px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">Person Name</TableHead>
                      <TableHead className="min-w-[80px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">Rate</TableHead>
                      <TableHead className="min-w-[60px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Apply All</TableHead>
                      {editWeekLabels.map((label, i) => (
                        <TableHead key={i} className="min-w-[80px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">
                          {label}
                        </TableHead>
                      ))}
                      <TableHead className="min-w-[100px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">Total</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editGroups.map((group, gIdx) => {
                      const plannedTotal = group.planned ? calcTotal([group.planned]) : 0;
                      const groupActualTotal = calcTotal(group.persons);
                      return (
                        <React.Fragment key={group.type}>
                          {/* Planned row (read-only, dimmed) */}
                          {group.planned && (
                            <TableRow className="border-zinc-900/30 hover:bg-transparent opacity-50">
                              <TableCell className="sticky left-0 z-10 bg-zinc-950 font-manrope text-xs text-zinc-400">
                                {group.planned.label} <span className="text-[10px] text-zinc-600">(planned)</span>
                              </TableCell>
                              <TableCell className="text-xs text-zinc-600">—</TableCell>
                              <TableCell className="font-mono text-xs text-zinc-500">${group.planned.rate}</TableCell>
                              <TableCell></TableCell>
                              {(group.planned.weeklyAllocation || []).map((val, wi) => (
                                <TableCell key={wi} className="text-center font-mono text-xs text-zinc-500">{val || 0}</TableCell>
                              ))}
                              <TableCell className="text-right font-mono text-xs text-zinc-500">
                                {formatCurrency(plannedTotal)}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          )}

                          {/* Actual person rows (editable) */}
                          {group.persons.map((person, pIdx) => {
                            const flatIdx = flatIndexMap[gIdx]?.[pIdx];
                            if (flatIdx === undefined || flatIdx === -1) return null;
                            const personTotal = (person.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (person.rate || 0);
                            const isOnlyPerson = group.persons.length === 1;
                            return (
                              <TableRow key={`${group.type}-${pIdx}`} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`avp-actual-row-${flatIdx}`}>
                                <TableCell className="sticky left-0 z-10 bg-zinc-950 font-manrope text-xs text-brand-400">
                                  {pIdx === 0 ? (
                                    <>{group.label} <span className="text-[10px] text-emerald-500">(actual)</span></>
                                  ) : (
                                    <span className="text-zinc-500 pl-2">↳ Person {pIdx + 1}</span>
                                  )}
                                </TableCell>
                                <TableCell className="p-1">
                                  <Input
                                    value={person.name}
                                    onChange={e => updatePersonName(flatIdx, e.target.value)}
                                    placeholder="Tag name"
                                    data-testid={`avp-name-${flatIdx}`}
                                    className="bg-zinc-950 border-zinc-800 font-manrope text-xs h-7 w-32 focus:border-brand-500"
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-xs text-zinc-300">${person.rate}</TableCell>
                                <TableCell className="text-center p-1">
                                  <Checkbox
                                    data-testid={`avp-apply-all-${flatIdx}`}
                                    checked={!!applyAllWeeks[flatIdx]}
                                    onCheckedChange={(checked) => toggleApplyAll(flatIdx, checked)}
                                    className="border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
                                  />
                                </TableCell>
                                {(person.weeklyAllocation || []).map((val, wi) => (
                                  <TableCell key={wi} className="p-1 text-center">
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      value={val || ''}
                                      onChange={e => updatePersonAllocation(flatIdx, wi, e.target.value)}
                                      placeholder="0"
                                      data-testid={`avp-alloc-${flatIdx}-${wi}`}
                                      className="bg-zinc-950 border-zinc-800 font-mono text-xs h-7 w-14 text-center mx-auto focus:border-brand-500 placeholder:text-zinc-700"
                                    />
                                  </TableCell>
                                ))}
                                <TableCell className="text-right">
                                  <span className="font-mono text-sm text-brand-400">{formatCurrency(personTotal)}</span>
                                </TableCell>
                                <TableCell className="p-1">
                                  {!isOnlyPerson && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removePerson(flatIdx)}
                                      data-testid={`avp-remove-person-${flatIdx}`}
                                      className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}

                          {/* Add Person button row */}
                          <TableRow className="border-zinc-900/20 hover:bg-transparent">
                            <TableCell colSpan={4 + editWeekLabels.length + 2} className="py-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addPerson(group.type, group.label, group.rate)}
                                data-testid={`avp-add-person-${group.type}`}
                                className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-[11px] h-6 px-2"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Person for {group.label}
                              </Button>
                            </TableCell>
                          </TableRow>

                          {/* Group subtotal row */}
                          {group.persons.length > 1 && (
                            <TableRow className="border-zinc-800 hover:bg-transparent bg-zinc-900/20">
                              <TableCell className="sticky left-0 z-10 bg-zinc-900/20 font-manrope text-[10px] uppercase tracking-widest text-zinc-500">
                                {group.label} Total
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              {Array.from({ length: editWeekLabels.length }).map((_, wi) => {
                                const weekSum = group.persons.reduce((s, p) => s + ((p.weeklyAllocation || [])[wi] || 0), 0);
                                return (
                                  <TableCell key={wi} className="text-center font-mono text-[11px] text-zinc-400">{weekSum}</TableCell>
                                );
                              })}
                              <TableCell className="text-right font-mono text-xs text-brand-400 font-semibold">
                                {formatCurrency(groupActualTotal)}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Add Spillover Week button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={addSpilloverWeek}
                data-testid="avp-add-week-btn"
                className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs mt-2"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Week (Spillover)
              </Button>
              {spilloverCount > 0 && (
                <p className="text-[10px] text-zinc-600 font-manrope mt-1">{spilloverCount} spillover week{spilloverCount !== 1 ? 's' : ''} added beyond planned timeline</p>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setEditProject(null)}
              className="text-zinc-400 hover:text-white hover:bg-zinc-900"
              data-testid="avp-cancel-btn"
            >
              <X className="w-4 h-4 mr-1.5" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-500 text-white hover:bg-brand-400 font-semibold"
              data-testid="avp-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Saving...' : 'Save Actuals'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
